// Cliente HTTP minimo para hablar con /api/v1. Auth via Authorization Bearer.

import type { FlyerDocument } from "../../../flyer-renderer/src/flyer/types";
import type {
  ActualizarBrandKitRequest,
  Asset,
  BrandKit,
  CatalogoModelos,
  EnvelopeError,
  EstadoGeneracion,
  Job,
  Proyecto,
  RespuestaEdicionFlyer,
  RespuestaRenderFlyer,
  RespuestaRevertirFlyer,
  RevisionFlyer,
  RespuestaAuth,
  SalidaFlyer,
  SolicitudActualizarProyecto,
  SolicitudCrearProyecto,
  Usuario,
} from "./types";

// Error tipado para poder distinguir 401 (sesion invalida) del resto.
export class ErrorApi extends Error {
  status: number;
  codigo: string;
  detalles?: Record<string, unknown> | null;

  constructor(
    mensaje: string,
    status: number,
    codigo: string,
    detalles?: Record<string, unknown> | null,
  ) {
    super(mensaje);
    this.name = "ErrorApi";
    this.status = status;
    this.codigo = codigo;
    this.detalles = detalles;
  }
}

const BASE = "/api/v1";

// Handler central de 401: useSesion lo registra para cerrar sesion y volver
// al login apenas cualquier request detecta un token invalido/expirado, sin
// que cada componente tenga que repetir esa logica.
type ManejadorNoAutorizado = () => void;
let manejadorNoAutorizado: ManejadorNoAutorizado | null = null;

export function registrarManejadorNoAutorizado(
  fn: ManejadorNoAutorizado | null,
): void {
  manejadorNoAutorizado = fn;
}

function cabecerasAuth(token: string): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Envoltura fetch: adjunta Bearer, parsea el envelope de error uniforme y
// devuelve JSON tipado. No captura errores de red: el llamador decide.
async function pedir<T>(
  ruta: string,
  token: string,
  opciones: RequestInit = {},
): Promise<T> {
  const cabeceras: Record<string, string> = {
    ...cabecerasAuth(token),
    ...(opciones.headers as Record<string, string> | undefined),
  };
  // FormData (subida de assets) fija su propio Content-Type con boundary;
  // fijarlo nosotros a mano rompe el parseo multipart del backend.
  if (
    opciones.body !== undefined &&
    !(opciones.body instanceof FormData) &&
    !("Content-Type" in cabeceras)
  ) {
    cabeceras["Content-Type"] = "application/json";
  }

  const respuesta = await fetch(`${BASE}${ruta}`, {
    ...opciones,
    headers: cabeceras,
  });

  if (!respuesta.ok) {
    // El backend devuelve {error: {code, message, details}}. Toleramos que no.
    let codigo = `http_${respuesta.status}`;
    let mensaje = respuesta.statusText || "Error HTTP";
    let detalles: Record<string, unknown> | null = null;
    try {
      const cuerpo = (await respuesta.json()) as EnvelopeError;
      if (cuerpo?.error) {
        codigo = cuerpo.error.code ?? codigo;
        mensaje = cuerpo.error.message ?? mensaje;
        detalles = cuerpo.error.details ?? null;
      }
    } catch {
      // No era JSON; nos quedamos con el mensaje generico.
    }
    if (respuesta.status === 401 && token) {
      // Solo dispara el cierre de sesion si la request llevaba token: un 401
      // en login/registro es "credenciales invalidas", no "sesion vencida".
      manejadorNoAutorizado?.();
    }
    throw new ErrorApi(mensaje, respuesta.status, codigo, detalles);
  }

  if (respuesta.status === 204) {
    return undefined as T;
  }
  return (await respuesta.json()) as T;
}

// ---------- Auth ----------

export function registrarUsuario(
  email: string,
  password: string,
): Promise<RespuestaAuth> {
  return pedir<RespuestaAuth>("/auth/register", "", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function iniciarSesion(
  email: string,
  password: string,
): Promise<RespuestaAuth> {
  return pedir<RespuestaAuth>("/auth/login", "", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export function obtenerUsuarioActual(token: string): Promise<Usuario> {
  return pedir<Usuario>("/auth/me", token, { method: "GET" });
}

// ---------- Catalogo de modelos/effort (nunca hardcodeado en el frontend) ----------

export function obtenerCatalogoModelos(): Promise<CatalogoModelos> {
  return pedir<CatalogoModelos>("/models", "", { method: "GET" });
}

// ---------- Proyectos ----------

export function crearProyecto(
  token: string,
  cuerpo: SolicitudCrearProyecto,
): Promise<Proyecto> {
  return pedir<Proyecto>("/projects", token, {
    method: "POST",
    body: JSON.stringify(cuerpo),
  });
}

export function listarProyectos(token: string): Promise<Proyecto[]> {
  return pedir<Proyecto[]>("/projects", token, { method: "GET" });
}

export function obtenerProyecto(token: string, id: string): Promise<Proyecto> {
  return pedir<Proyecto>(`/projects/${id}`, token, { method: "GET" });
}

export function actualizarProyecto(
  token: string,
  id: string,
  cuerpo: SolicitudActualizarProyecto,
): Promise<Proyecto> {
  return pedir<Proyecto>(`/projects/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(cuerpo),
  });
}

// ---------- Generacion ----------

export function iniciarGeneracion(token: string, id: string): Promise<Job> {
  return pedir<Job>(`/projects/${id}/generate`, token, { method: "POST" });
}

export function obtenerEstadoGeneracion(
  token: string,
  id: string,
): Promise<EstadoGeneracion> {
  return pedir<EstadoGeneracion>(`/projects/${id}/generation`, token, {
    method: "GET",
  });
}

// ---------- Re-composicion por formato (M6) ----------
// El formato ORIGINAL del proyecto (Project.format/flyer_document) nunca se
// toca aca: `POST /recompose` guarda el resultado en un FlyerOutput aparte
// (ver backend/app/routers/generation.py). El progreso se sondea con el
// MISMO `obtenerEstadoGeneracion` de arriba (devuelve el job mas reciente sin
// filtrar por kind).

export function iniciarRecomposicion(
  token: string,
  id: string,
  format: string,
): Promise<Job> {
  return pedir<Job>(`/projects/${id}/recompose`, token, {
    method: "POST",
    body: JSON.stringify({ format }),
  });
}

export function listarSalidasFlyer(
  token: string,
  id: string,
): Promise<SalidaFlyer[]> {
  return pedir<SalidaFlyer[]>(`/projects/${id}/flyer-outputs`, token, {
    method: "GET",
  });
}

// ---------- Edicion del flyer + revisiones (M4) ----------

export function actualizarFlyer(
  token: string,
  id: string,
  flyerDocument: FlyerDocument,
): Promise<RespuestaEdicionFlyer> {
  return pedir<RespuestaEdicionFlyer>(`/projects/${id}/flyer`, token, {
    method: "PATCH",
    body: JSON.stringify({ flyer_document: flyerDocument }),
  });
}

export function listarRevisionesFlyer(
  token: string,
  id: string,
): Promise<RevisionFlyer[]> {
  return pedir<RevisionFlyer[]>(`/projects/${id}/revisions`, token, {
    method: "GET",
  });
}

export function revertirFlyer(
  token: string,
  id: string,
  version: number,
): Promise<RespuestaRevertirFlyer> {
  return pedir<RespuestaRevertirFlyer>(
    `/projects/${id}/revisions/${version}/revert`,
    token,
    { method: "POST" },
  );
}

export function rerenderizarFlyer(
  token: string,
  id: string,
): Promise<RespuestaRenderFlyer> {
  return pedir<RespuestaRenderFlyer>(`/projects/${id}/render`, token, {
    method: "POST",
  });
}

// ---------- Assets + kit de marca (M5b) ----------

export function subirAsset(
  token: string,
  proyectoId: string,
  archivo: File,
): Promise<Asset> {
  const datos = new FormData();
  datos.append("file", archivo);
  return pedir<Asset>(`/projects/${proyectoId}/assets`, token, {
    method: "POST",
    body: datos,
  });
}

export function listarAssets(
  token: string,
  proyectoId: string,
): Promise<Asset[]> {
  return pedir<Asset[]>(`/projects/${proyectoId}/assets`, token, {
    method: "GET",
  });
}

export function borrarAsset(
  token: string,
  proyectoId: string,
  assetId: string,
): Promise<void> {
  return pedir<void>(`/projects/${proyectoId}/assets/${assetId}`, token, {
    method: "DELETE",
  });
}

export function quitarFondoAsset(
  token: string,
  proyectoId: string,
  assetId: string,
): Promise<Asset> {
  return pedir<Asset>(
    `/projects/${proyectoId}/assets/${assetId}/remove-background`,
    token,
    { method: "POST" },
  );
}

export function obtenerBrandKit(
  token: string,
  proyectoId: string,
): Promise<BrandKit> {
  return pedir<BrandKit>(`/projects/${proyectoId}/brand-kit`, token, {
    method: "GET",
  });
}

export function actualizarBrandKit(
  token: string,
  proyectoId: string,
  cuerpo: ActualizarBrandKitRequest,
): Promise<BrandKit> {
  return pedir<BrandKit>(`/projects/${proyectoId}/brand-kit`, token, {
    method: "PATCH",
    body: JSON.stringify(cuerpo),
  });
}
